import { IQuantityPerUnit, TimePeriod } from '@alberta/konexi-shared';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { ArticleViewModel } from 'src/app/business/article/view-models/article-view-model';
import { returnDurationText } from 'src/app/business/general/duration2text-factory';
import { articleIsForUse } from 'src/app/business/order/article-is-for-use-helper';
import { ShoppingCartArticleViewModel } from 'src/app/business/shopping-cart/shopping-cart-article-view-model';
import { map } from 'src/app/common/mapper/mapper';
import { ArticleUnitPipe } from 'src/app/common/pipes/article/articleunit-pipe';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { ArticleService } from 'src/app/shared/services/article.service';

import { AddArticleService } from '../services/add-article.service';

export const DayToYearDurations = [
  TimePeriod.Day,
  TimePeriod.Week,
  TimePeriod.Month,
  TimePeriod.HalfYear,
  TimePeriod.Year,
];

@Component({
  selector: 'itl-add-article',
  templateUrl: './add-article.component.html',
  styleUrls: ['./add-article.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AddArticleService],
})
export class AddArticleComponent implements OnInit, OnDestroy {
  @Input() public articleLine: ShoppingCartArticleViewModel;
  @Input() public displayAnnotation: boolean;
  @Input() public annotationLimit: number;
  @Input() public maxOrderAmount = -1;

  public form: UntypedFormGroup;
  public quantity: AbstractControl;
  public interval: AbstractControl;
  public packagingId: AbstractControl;
  public period: AbstractControl;
  public annotation: AbstractControl;

  public article: ArticleViewModel = new ArticleViewModel();

  public units: SelectionItem[] = [];
  public periods: SelectionItem[] = [];

  get showIntervall(): boolean {
    return !articleIsForUse(this.article);
  }

  private ngUnsubscribe = new Subject();

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _modalCtrl: ModalController,
    private _translate: TranslateService,
    private _articleService: ArticleService,
    private _articleUnitPipe: ArticleUnitPipe,
    private _alertController: AlertController,
    private _addArticleService: AddArticleService,
    private _changeDetectionRef: ChangeDetectorRef
  ) {
    const durationSelectionItems = DayToYearDurations.map(
      timePeriod => new SelectionItem(timePeriod, returnDurationText(this._translate, timePeriod, true))
    );
    this.periods.push(...durationSelectionItems);
  }

  public async ngOnInit(): Promise<void> {
    this.initializeFormBuilder();
    this.form.controls['packagingId'].markAsTouched();
    const articleDto = await this._articleService.find(this.articleLine.articleId);
    if (!articleDto) {
      await this.showArticleNotFoundAlert();
    }
    this.article = map(articleDto, new ArticleViewModel());
    const packingUnitsSelectionItems = this.getAvailablePackagingUnits().map(packagingUnit =>
      this.createPackagingUnitItem(packagingUnit)
    );
    this.units.push(...packingUnitsSelectionItems);
    if (this.units.length === 1) {
      this.articleLine.dosage.packagingId = this.units[0].id;
      this.packagingId.setValue(this.units[0].id);
    } else if (this.units.length > 1) {
      const defaultPackagingUnit = this.article.packagingUnits.find((f: any) => f.defaultPackagingUnit);
      if (defaultPackagingUnit) {
        this.articleLine.dosage.packagingId = defaultPackagingUnit._id;
        this.packagingId.setValue(defaultPackagingUnit._id);
      }
    }
    this._changeDetectionRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  private async showArticleNotFoundAlert() {
    const alert = await this._alertController.create({
      backdropDismiss: false,
      header: 'Artikel nicht gefunden',
      message: 'Der gesuchte Artikel konnte nicht gefunden werden.',
      buttons: [
        {
          text: this._translate.instant('DONE_BUTTON'),
          handler: () => this.cancel(),
        },
      ],
    });
    await alert.present();
    await alert.onDidDismiss();
    throw new Error(`Article with _id ${this.articleLine.articleId} could not be found.`);
  }

  private getAvailablePackagingUnits() {
    return this.article.packagingUnits.filter(packagingUnit => !packagingUnit.archived);
  }

  private createPackagingUnitItem(packagingUnit: IQuantityPerUnit): SelectionItem {
    const inhalt = this._translate.instant('amount');
    const stueck = this._translate.instant('article.edit.units.stk');
    const pcnText = packagingUnit.pcn ? `${this._translate.instant('article.pcn')} ${packagingUnit.pcn}` : '';
    return new SelectionItem(
      packagingUnit._id,
      `${this._articleUnitPipe.transform(packagingUnit.unit)} (${inhalt}: ${
        packagingUnit.quantity
      } ${stueck})\n${pcnText}`
    );
  }

  private initializeFormBuilder() {
    this.form = this._formBuilder.group(
      {
        _id: [this.articleLine._id],
        quantity: [this.articleLine.dosage.quantity, [Validators.required, Validators.min(1)]],
        interval: [this.articleLine.dosage.duration.amount, [Validators.required, Validators.min(1)]],
        packagingId: [this.articleLine.dosage.packagingId, Validators.required],
        period: [this.articleLine.dosage.duration.timePeriod, Validators.required],
        annotation: [this.articleLine.annotation],
      },
      { validators: this.validateOrderAmount() }
    );
    this.quantity = this.form.controls['quantity'];
    this.interval = this.form.controls['interval'];
    this.packagingId = this.form.controls['packagingId'];
    this.period = this.form.controls['period'];
    this.annotation = this.form.controls['annotation'];
  }

  private validateOrderAmount() {
    return (control: UntypedFormGroup): { [key: string]: any } | null => {
      return this._addArticleService.calcIfMaxAmountIsExceeded(control, this.article, this.maxOrderAmount);
    };
  }

  public async save(): Promise<void> {
    if (this.form.valid) {
      await this._modalCtrl.dismiss(this.articleLine).catch(error => console.log(error));
    }
  }
  public async cancel(): Promise<void> {
    await this._modalCtrl.dismiss().catch(error => console.log(error));
  }
}
